import React, { FC, useContext, useEffect, useRef, useState } from 'react'
import { Button, Card, Spinner } from 'react-bootstrap'
import { generateTmpAudio } from '@editor/store/thunks/audio.thunk'
import {
  EditorContext,
  EditorContextType,
} from '@editor/components/editorContext'
import { toast } from 'react-toastify'
import { unwrapResult } from '@reduxjs/toolkit'
import CustomAudioPlayer from '@editor/components/misc/customAudioPlayer'

const SelectionBar: FC = () => {
  const { dispatch, tags, selectedPathTags, selectedRange, setSelectedRange } = useContext(
    EditorContext
  ) as EditorContextType
  const [audioUrl, setAudioUrl] = useState<string | undefined>(undefined)
  const selectedRef = useRef<HTMLDivElement>(null)
  const [smallSsml, setSmallSsml] = useState<string>('')
  const [audioLoading, setAudioLoading] = useState<boolean>(false)
  const [hasWindowSelection, setHasWindowSelection] = useState(false)

  useEffect(() => {
    const handleSelectionChange = () => {
      const selection = window.getSelection()
      setHasWindowSelection(!!selection && selection.toString().length > 0)
    }

    document.addEventListener('selectionchange', handleSelectionChange)

    return () => {
      document.removeEventListener('selectionchange', handleSelectionChange)
    }
  }, [])

  useEffect(() => {
    if (
      selectedRange &&
      selectedRange.toString().length > 0 &&
      selectedPathTags &&
      selectedPathTags.length > 0
    ) {
      const selectedContents = selectedRange.cloneContents()
      const div = document.createElement('div')
      div.appendChild(selectedContents)
      cleanHTML(div)
      setSmallSsml(wrapWithTags(div.innerHTML))
      div.remove()
    }
  }, [selectedRange, selectedPathTags])

  useEffect(() => {
    const selectedElement = selectedRef.current
    if (selectedElement) {
      selectedElement.innerHTML = smallSsml
    }
  }, [smallSsml])

  useEffect(() => {
    if (smallSsml) {
      getAudio()
    }
  }, [smallSsml])

  const cleanText = (ssml: string): string => {
    return ssml.replace(/&nbsp;/g, ' ')
  }

  const getAudio = () => {
    setAudioLoading(true)
    dispatch(generateTmpAudio(cleanText(smallSsml)))
      .then(unwrapResult)
      .then(onGetAudioSuccess)
      .catch(onGetAudioError)
      .finally(() => setAudioLoading(false))
  }

  const onGetAudioSuccess = (tmpAudio: any) => {
    if (tmpAudio && tmpAudio.audioUrl) {
      setAudioUrl(tmpAudio.audioUrl)
    } else {
      toast.error('Zvuková stopa je nedostupná')
      throw new Error('Zvuková stopa je nedostupná')
    }
  }

  const onGetAudioError = (error: any) => {
    toast.error(error)
    console.error(`Audio úsek se nepodařilo vytvořit`, error)
  }

  const cleanHTML = (element: Node) => {
    const nodes = Array.from(element.childNodes)
    nodes.forEach((node) => {
      if (node.nodeType === Node.ELEMENT_NODE) {
        cleanHTML(node)

        const isSpeakElement = node.nodeName.toLowerCase() === 'speak'
        const isAllowed = tags.some(
          (tag) => tag.tagName.toLowerCase() === node.nodeName.toLowerCase()
        )

        // The function prepares a inner ssml structure for temp audio generation.
        // The new ssml structure might introduce non-parent speak elements there, this will remove them
        if (isSpeakElement || !isAllowed) {
          while (node.firstChild) {
            node.parentNode?.insertBefore(node.firstChild, node)
          }
          node.parentNode?.removeChild(node)
        }
      }
    })
  }

  const wrapWithTags = (text: string) => {
    // Wrap the text with all the tags, starting from the innermost tag
    const pathTags = [...selectedPathTags]

    // New ssml structure will introduce second speak element before each paragraph - remove first one to prevent tts error
    if (
      pathTags.length > 2 &&
      pathTags[0].tagName === 'speak' &&
      pathTags[1].tagName === 'speak'
    ) {
      pathTags.shift()
    }
    const reversedPathTags = [...pathTags].reverse()

    // Wrap the text with all the tags
    return reversedPathTags.reduce((wrappedText, tagModel) => {
      const attributes = tagModel.tagAttributes
        .map((attr) => `${attr.attributeName}="${attr.attributeValue}"`)
        .join(' ')

      return `<${tagModel.tagName} ${attributes}>${wrappedText}</${tagModel.tagName}>`
    }, text)
  }

  const onPlaySelectionClick = () => {
    const windowSelection = window.getSelection()
    if (windowSelection && windowSelection.toString().length > 0) {
      dispatch(generateTmpAudio(cleanText(windowSelection.toString())))
      setSelectedRange(windowSelection.getRangeAt(0))
    }
  }

  return (
    <>
      <Card className="my-3 p-0">
        {!!selectedRange && selectedRange.toString().length > 0 && (
          <Card.Body>
            <Card.Title>Vybraný text</Card.Title>
            <div className="my-2 styled-custom-tags" ref={selectedRef}></div>
            {audioUrl && (
              <CustomAudioPlayer
                audioSrc={audioUrl}
                showSpeedBar={false}
              ></CustomAudioPlayer>
            )}
            {audioLoading && selectedPathTags && (
              <Button variant="primary" disabled>
                <Spinner
                  as="span"
                  animation="border"
                  size="sm"
                  role="status"
                  aria-hidden="true"
                />
                <span className="sr-only ms-2"> Načítání audia...</span>
              </Button>
            )}
            {!audioLoading && !audioUrl && (
              <span className="sr-only ms-2 text-danger">
                Audio se nepodařilo vygenerovat
              </span>
            )}
          </Card.Body>
        )}
        <Button
          style={{
            margin: '6px auto',
            width: '60%',
          }}
          onClick={() => onPlaySelectionClick()}
          disabled={!hasWindowSelection}
        >
          Použít nový výběr
        </Button>
      </Card>
    </>
  )
}

export default SelectionBar
